import React, { useState } from "react";
import { Field, ErrorMessage, useField } from "formik";
import styled from "styled-components";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import { Tooltip } from "antd";
import { Typography } from "@mui/material";
import type { TooltipPlacement } from "antd/es/tooltip";
import SearchIcon from "@mui/icons-material/Search";
import { Box } from "@mui/material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

export interface InputFieldProps {
  label: string;
  name: string;
  type?: string;
  placeholder?: string;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  value?: string | number;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  disabled?: boolean;
  isRequired?: boolean;
  min?: string | number;
  max?: string | number;
  height?: string | any;
  labelMb?: number | string; // label row margin-bottom
  labelWeight?: number | string; // label font weight
  labelSize?: number | string; // label font-size (default 14px)
  showHelp?: boolean; // show the ? icon
  helpText?: string; // tooltip text
  helpPlacement?: TooltipPlacement; // tooltip placement (default topRight)
  searchIcon?: boolean;
  is_phone?: boolean;
  inputBoxSize?: "small" | "medium" | "large";
  endAdornment?: React.ReactNode;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  isColorPicker?: boolean;
  isFilter?: boolean;
  onReset?: () => void;
  inputRef?: React.RefObject<HTMLInputElement>;
  hideLabel?: boolean; // hide the label row
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  maxLength?: number;
  multiline?: boolean;
  rows?: number;
}

const FieldContainer = styled.div`
  font-family: "Poppins", sans-serif;
`;

const LabelRow = styled.div<{ $mb?: number | string }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ $mb }) => ($mb !== undefined ? $mb : "4px")};
`;

const StyledLabel = styled.label<{
  $weight?: number | string;
  $size?: number | string;
}>`
  display: block;
  font-weight: ${({ $weight }) => ($weight !== undefined ? $weight : 600)};
  font-size: ${({ $size }) =>
    $size !== undefined
      ? typeof $size === "number"
        ? `${$size}px`
        : $size
      : "14px"};
`;

const StyledError = styled.div`
  color: red;
  font-size: 0.875rem;
  margin-top: 0.25rem;
`;

// const StyledField = styled(Field) <{ $error?: boolean; $height?: string | any }>`
//   position: relative;
//   width: 100%;
//   box-sizing: border-box;
//   padding: 10px;
//   font-size: 14px; /* fixed */
//   border: 1px solid ${({ $error }) => ($error ? "red" : "#ccc")};
//   border-radius: 5px;
//   outline: none;
//   transition: border-color 0.3s, box-shadow 0.3s;
//   height: ${({ $height }) => $height || ""};

//   &:focus {
//     border-color: ${({ $error }) => ($error ? "red" : "#007bff")} !important;
//     box-shadow: ${({ $error }) =>
//     $error
//       ? "0 0 5px rgba(255, 0, 0, 0.5)"
//       : "0 0 5px rgba(0, 123, 255, 0.5)"} !important;
//     outline: none;
//   }
// `;

const StyledField = styled(Field)<{
  $error?: boolean;
  $height?: string | any;
  $inputBoxSize?: "small" | "medium" | "large";
  $hasIcon?: boolean;
  $is_phone?: boolean;
}>`
  position: relative;
  width: 100%;
  box-sizing: border-box;
  padding: ${({ $inputBoxSize, $hasIcon }) => {
    const leftPadding = $hasIcon ? "36px" : "10px";
    switch ($inputBoxSize) {
      case "small":
        return `6px 10px 6px ${leftPadding}`;
      case "large":
        return `14px 10px 14px ${leftPadding}`;
      default:
        return `10px 10px 10px ${leftPadding}`;
    }
  }};

  font-size: ${({ $inputBoxSize }) => {
    switch ($inputBoxSize) {
      case "small":
        return "12px";
      case "large":
        return "16px";
      default:
        return "14px";
    }
  }};

  font-size: 12px !important;
  font-family: "Poppins", sans-serif !important;

  border: 1px solid ${({ $error }) => ($error ? "red" : "#ccc")};
  border-radius: ${({ $is_phone }) => ($is_phone ? "0 !important" : "5px")};
  outline: none;
  transition: border-color 0.3s, box-shadow 0.3s;
  height: ${({ $height }) => $height || "auto"};

  &::placeholder {
    color: #999 !important;
    opacity: 1;
    font-family: "Poppins", sans-serif !important;
    font-size: ${({ $inputBoxSize }) =>
      $inputBoxSize === "large" ? "14px" : "13px"} !important;
  }

  &:focus {
    border-color: ${({ $error }) => ($error ? "red" : "#007bff")} !important;
    box-shadow: ${({ $error }) =>
      $error
        ? "0 0 5px rgba(255, 0, 0, 0.5)"
        : "0 0 5px rgba(0, 123, 255, 0.5)"} !important;
    outline: none;
  }
`;

const ResetIconWrapper = styled.div`
  position: absolute;
  top: 55%;
  right: 10px;
  transform: translateY(-50%);
  font-size: 14px;
  color: #555;
  cursor: pointer;
  z-index: 10;
`;

// const SearchIconWrapper = styled.div`
//   position: absolute;
//   top: 50%;
//   left: 10px;
//   transform: translateY(-50%);
//   pointer-events: none;
//   color: #555;
// `;

const InputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const EyeButton = styled.button`
  position: absolute;
  top: 53%;
  right: 10px;
  transform: translateY(-50%);
  border: none;
  cursor: pointer;
  background-color: transparent;
`;

const EndAdornmentWrapper = styled.div`
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  font-size: 12px;
  font-weight: 600;
  // color: #1976d2;
  color: #000000;
  cursor: pointer;
`;

// small rounded "?" icon
const HelpIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border: 1px solid #cfcfcf;
  border-radius: 50%;
  font-size: 11px;
  color: #8b8b8b;
  cursor: pointer;
  user-select: none;
`;

export const InputField: React.FC<InputFieldProps> = ({
  label,
  name,
  type = "text",
  placeholder,
  onKeyDown,
  value,
  onChange,
  onBlur,
  height,
  disabled = false,
  isRequired = false,
  min,
  max,
  // Optional props with defaults
  labelMb,
  labelWeight,
  labelSize = 14, // default 14px for label
  showHelp = false,
  helpText,
  helpPlacement = "topRight",
  searchIcon = false,
  is_phone = false,
  inputBoxSize = "medium",
  endAdornment,
  onFocus,
  isColorPicker = false,
  isFilter = false,
  onReset,
  inputRef,
  hideLabel = false,
  inputProps,
  maxLength
}) => {
  const [field, meta] = useField(name);
  const [showPassword, setShowPassword] = useState(false);
  const actualType = type === "password" && showPassword ? "text" : type;

  const togglePasswordVisibility = () => setShowPassword((p) => !p);

  return (
    <FieldContainer>
      {!hideLabel && (
      <LabelRow $mb={labelMb}>
        <StyledLabel htmlFor={name} $weight={labelWeight} $size={labelSize}>
          {label}
          {isRequired && (
            <span style={{ color: "red", fontSize: "15px" }}>*</span>
          )}
        </StyledLabel>

        {showHelp && !!helpText && (
          <Tooltip
            title={helpText}
            placement={helpPlacement}
            zIndex={2000}
            // getPopupContainer={(trigger) => trigger?.parentElement || document.body}
          >
            <HelpIcon aria-label="help">?</HelpIcon>
          </Tooltip>
        )}
      </LabelRow>
      )}

      <InputWrapper>
        {searchIcon && (
          <SearchIcon
            style={{
              position: "absolute",
              left: 10,
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 1,
              color: "#2b2a2aff",
            }}
          />
        )}

        {is_phone ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              width: "100%",
              border: `1px solid ${
                !!(meta?.touched && meta?.error) ? "red" : "#ccc"
              }`,
              borderRadius: "5px",
              overflow: "hidden",
              transition: "all 0.2s ease-in-out",
              "&:focus-within": {
                borderColor: !!(meta?.touched && meta?.error)
                  ? "red"
                  : "#007bff",
                boxShadow: !!(meta?.touched && meta?.error)
                  ? "0 0 5px rgba(255, 0, 0, 0.5)"
                  : "0 0 5px rgba(0, 123, 255, 0.5)",
                outline: "none",
              },
            }}
          >
            {/* Prefix section */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                px: 1.5,
                backgroundColor: "#f7f7f7",
                height: height,
                flexShrink: 0,
                borderRight: "1px solid #ccc",
              }}
            >
              <Typography
                fontSize="clamp(12px, 1.5vw, 14px)"
                sx={{ color: "#1A1A1A", userSelect: "none" }}
              >
                +91
              </Typography>
            </Box>

            {/* Actual input field */}
            <StyledField
              id={name}
              name={name}
              type={actualType}
              placeholder={placeholder || "94585 874561"}
              onKeyDown={onKeyDown}
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              disabled={disabled}
              min={min}
              max={max}
              $error={!!(meta.touched && meta.error)}
              $height={height}
              $inputBoxSize={inputBoxSize}
              $hasIcon={searchIcon}
              $is_phone={is_phone}
              style={{
                border: "none",
                outline: "none",
                flex: 1,
                borderRadius: 0,
                background: "transparent",
              }}
            />
          </Box>
        ) : isColorPicker ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              width: "100%",
              border: `1px solid ${
                !!(meta?.touched && meta?.error) ? "red" : "#ccc"
              }`,
              borderRadius: "5px",
              overflow: "hidden",
              transition: "all 0.2s ease-in-out",
              "&:focus-within": {
                borderColor: !!(meta?.touched && meta?.error)
                  ? "red"
                  : "#007bff",
                boxShadow: !!(meta?.touched && meta?.error)
                  ? "0 0 5px rgba(255, 0, 0, 0.5)"
                  : "0 0 5px rgba(0, 123, 255, 0.5)",
                outline: "none",
              },
            }}
          >
            <Typography
              variant="body2"
              sx={{
                width: "20%",
                fontFamily: "Poppins, sans-serif",
                color: "#333",
                fontSize: "14px",
                textAlign: "center",
              }}
            >
              {field.value || "#00B14E"}
            </Typography>
            <StyledField
              id={name}
              name={name}
              type="color"
              onChange={onChange || field.onChange}
              value={value || field.value}
              disabled={disabled}
              onBlur={onBlur}
              $error={!!(meta.touched && meta.error)}
              $height={height || "40px"}
              $inputBoxSize={inputBoxSize}
              style={{
                width: "80%",
                height: "40px",
                padding: 0,
                // borderRadius: "5px",
                cursor: "pointer",
                backgroundColor: field.value,
              }}
            />
          </Box>
        ) : isFilter ? (
          <>
            <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
              {isFilter && field.value && (
                <ResetIconWrapper onClick={onReset}>
                  <RestartAltIcon color="primary" sx={{ fontSize: 16 }} />
                </ResetIconWrapper>
              )}
              <StyledField
                inputRef={inputRef}
                id={name}
                name={name}
                type={actualType}
                placeholder={placeholder}
                onKeyDown={onKeyDown}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                disabled={disabled}
                min={min}
                max={max}
                $error={!!(meta.touched && meta.error)}
                $height={height}
                $inputBoxSize={inputBoxSize}
                $hasIcon={searchIcon}
                onFocus={onFocus}
              />
            </Box>
          </>
        ) : (
          <StyledField
            inputRef={inputRef}
            id={name}
            name={name}
            type={actualType}
            placeholder={placeholder}
            onKeyDown={onKeyDown}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            disabled={disabled}
            min={min}
            max={max}
            $error={!!(meta.touched && meta.error)}
            $height={height}
            $inputBoxSize={inputBoxSize}
            $hasIcon={searchIcon}
            onFocus={onFocus}
            maxLength={maxLength}
            {...inputProps}
          />
        )}

        {type === "password" && (
          <EyeButton
            onClick={togglePasswordVisibility}
            type="button"
            aria-label="toggle password"
          >
            {showPassword ? (
              <VisibilityOffOutlinedIcon sx={{ fontSize: "18px" }} />
            ) : (
              <VisibilityOutlinedIcon sx={{ fontSize: "18px" }} />
            )}
          </EyeButton>
        )}

        {endAdornment && (
          <EndAdornmentWrapper>{endAdornment}</EndAdornmentWrapper>
        )}
      </InputWrapper>

      <ErrorMessage name={name} component={StyledError} />
    </FieldContainer>
  );
};